/*    
<summary>
   This functional component provides us functionalities like redirection to other modules, logout & User Profile.
   Developer:Aashish Singh, Created Date:29-March-2023
</summary>
<returns>Returns JSX</returns>
*/
import { observer } from "mobx-react";
import React, { useEffect, useRef, useState } from "react";
import { FormattedMessage, injectIntl } from "react-intl";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import Huckster_Small_icon from "../../assets/Images/Huckster_Small_icon.png";
import mainLogo from "../../assets/Images/logo.png";
import { ModuleType } from "../../constants/enums/module-type-enum";
import userTypeEnum from "../../constants/enums/user-type-enum";
import { useStore } from "../../contexts/store-provider";
import {
  getCurrentSidebarExpansionFromLocalStorage,
  getIsTenantAccessFromLocalStorage,
  getStoreCodeFromLocalStorage,
} from "../../helpers/localstorage-helper";
import {
  findModueOptionsExist,
  findRouteAccess,
} from "../../helpers/route-helper";
import LogoutPopup from "../../modules/auth/components/logout-popup";
import LogoutPopupHOC from "../../modules/auth/hoc/logout-popup-hoc";
import UserProfilePopupHOC from "../../modules/auth/hoc/user-profile-popup-hoc";
import UserProfilePopup from "../../modules/auth/user-profile-popup";
import BusinessTypeSettings from "../../modules/master-management/master-settings/business-type-settings/business-type-settings";
import TaxSettings from "../../modules/master-management/master-settings/tax-settings/tax-settings";
import TimeSettings from "../../modules/master-management/master-settings/time-settings/time-settings";
import { formatMessage } from "../../translations/format-message";
import BusinessTypeSettingPopupHOC from "../hoc/business-type-popup-hoc";
import TaxSettingPopupHOC from "../hoc/tax-setting-popup-hoc";
import TimeSettingPopupHOC from "../hoc/time-setting-popup-hoc";
import RoutesConstants from "../routes/routes-constants";
import "./left-navbar.css";
import { emptyValueNA } from "../../constants/constants";
import {
  reservationSettingsRoutes,
  storeRoutes,
  totallingManagementRoutes,
  useIsRouteGroupActive,
  variousSettingsRoutes,
} from "./active-routes";
import { useConfirmDialog } from "../popup/smr-confirmation/smr-confirmation";

interface IProps {
  changeLanguage: (lang: string) => void;
  showTimeSettingPopup: boolean;
  timeSettingPopupToggleHandler: () => void;
  showTaxSettingPopup: boolean;
  taxSettingPopupToggleHandler: () => void;
  showBusinessTypeSettingPopup: boolean;
  businessTypeSettingPopupToggleHandler: () => void;
}

const LeftNavBar = (props: any) => {
  /* eslint-disable jsx-a11y/anchor-is-valid */
  const history = useHistory();
  const { authStore, smrStore } = useStore();
  const myRef = useRef<any>();
  const { getUserType } = authStore;

  const storeCode = getStoreCodeFromLocalStorage();

  const { showConfirmDialog, ConfirmDialogComponent } = useConfirmDialog();

  const handleChange = () => {
    const div = document.getElementById("left-sidebarNav");
    const move = document.getElementById("moveContent");
    if (div) {
      if (!div.className.includes("closeNav")) {
        div.classList.add("closeNav");
        if (move) move.classList.remove("ShiftLeftSide");
        localStorage.setItem("lizera_sidebarClosed", "true");
      } else {
        div.classList.remove("closeNav");
        if (move) move.classList.add("ShiftLeftSide");
        localStorage.setItem("lizera_sidebarClosed", "false");
      }
    }
  };

  const isStoreActive = useIsRouteGroupActive(storeRoutes);
  const isVariousSettingsActive = useIsRouteGroupActive(variousSettingsRoutes);
  const isReservationSettingsActive = useIsRouteGroupActive(
    reservationSettingsRoutes
  );
  const isTotallingManagementRoutes = useIsRouteGroupActive(
    totallingManagementRoutes
  );

  const location = useLocation();

  // Custom NavLink component with unsaved changes check
  const NavLinkWithCheck = ({ to, children, ...rest }: any) => {
    const handleClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
      const currentTarget = e.currentTarget; // 👈 store reference immediately

      if (smrStore.isDataChanged) {
        e.preventDefault();

        const confirmed = await showConfirmDialog(
          "confirmation",
          "unsaved_changes_warning"
        );

        if (confirmed) {
          smrStore.setIsDataChanged(false);
          history.push((to as any).pathname || to);
        } else {
          return; // stop further execution if not confirmed
        }
      }

        const sidebar = document.getElementById("left-sidebarNav");
        const parentLi = currentTarget.closest("li.dropdown") ||
          currentTarget.closest(".dropdown");

        // Add active state
        document
          .querySelectorAll(
            "#left-sidebarNav li a.active, #left-sidebarNav li.dropdown.active"
          )
          .forEach((el) => el.classList.remove("active"));

        currentTarget.classList.add("active");
        if (parentLi) parentLi.classList.add("active");

        // If sidebar is collapsed -> close dropdown after click
        if (sidebar?.classList.contains("closeNav")) {
          const dropdownMenu = parentLi?.querySelector(".dropdown-menu.show");
          if (dropdownMenu) dropdownMenu.classList.remove("show");
          const toggle = parentLi?.querySelector(".dropdown-toggle.show");
          if (toggle) toggle.classList.remove("show");
        }
    };

    return (
      <NavLink to={to} onClick={handleClick} {...rest}>
        {children}
      </NavLink>
    );
  };


  /**
   * ✅ On route change:
   * - When sidebar is collapsed, force-close dropdowns
   * - Maintain active classes for current route
   */
  useEffect(() => {
  const sidebar = document.getElementById("left-sidebarNav");

  // Remove previous active states
  document
    .querySelectorAll("#left-sidebarNav li a.active, #left-sidebarNav li.dropdown.active")
    .forEach((el) => el.classList.remove("active"));

  // Highlight active link based on current path
  const activeLink = document.querySelector(
    `#left-sidebarNav a[href='${location.pathname}']`
  );
  if (activeLink) {
    activeLink.classList.add("active");
    const parentDropdown = activeLink.closest("li.dropdown");
    if (parentDropdown) parentDropdown.classList.add("active");
  }

  // If sidebar is collapsed, close all open dropdowns
  if (sidebar?.classList.contains("closeNav")) {
    const openMenus = document.querySelectorAll(
      "#left-sidebarNav .dropdown-menu.show, #left-sidebarNav .dropdown-toggle.show"
    );
    openMenus.forEach((el) => el.classList.remove("show"));
  }
}, [location.pathname]);

  return (
    <React.Fragment>
      {props.showTimeSettingPopup && (
        <TimeSettings
          modalClosed={() => props.timeSettingPopupToggleHandler()}
        />
      )}
      <ConfirmDialogComponent />
      {props.showBusinessTypeSettingPopup && (
        <BusinessTypeSettings
          modalClosed={() => props.businessTypeSettingPopupToggleHandler()}
        />
      )}
      {props.showTaxSettingPopup && (
        <TaxSettings modalClosed={() => props.taxSettingPopupToggleHandler()} />
      )}

      {props.showUserProfilePopup && (
        <UserProfilePopup
          userName={props.email}
          userType={props.userType}
          modalClosed={props.UserProfileToggleHandler}
        />
      )}
      {props.showLogoutPopup && (
        <LogoutPopup modalClosed={props.logoutPopupToggleHandler} />
      )}

      <aside
        id="left-sidebarNav"
        ref={myRef}
        className={`${
          getCurrentSidebarExpansionFromLocalStorage() ? "closeNav" : ""
        }`}
      >
        <button
          id="nav-toogle"
          className="menu-toggle"
          onClick={handleChange}
          title={formatMessage("menu")}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
        <div className="nav-inner">
          <nav>
            <div className="sidebar-header">
              <div
                className="sidebar-logo"
                title={formatMessage("application_title")}
              >
                <img src={mainLogo} className="openNav-logo img-fluid"></img>
                <img
                  src={Huckster_Small_icon}
                  className="closeNav-logo img-fluid"
                ></img>
              </div>
            </div>
            <ul className="list-unstyled">
              <React.Fragment>
                {/* Store Headquater side  */}
                {findRouteAccess(ModuleType.Dashboard) && (
                  <li title={formatMessage("dashboard")}>
                    <NavLinkWithCheck to={RoutesConstants.Dashboard}>
                      <span className="svgWidth">
                        <svg id="Layer_1" width="22" height="22" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" data-name="Layer 1"><path d="m60 42.44v-32.95c0-2.12-1.72-3.84-3.84-3.84h-48.32c-2.12 0-3.84 1.72-3.84 3.84v32.95c0 2.12 1.72 3.84 3.84 3.84h14.71c.01 0 .03-.01.04-.01h18.82c.01 0 .03.01.04.01h14.71c2.12 0 3.84-1.72 3.84-3.84zm-9.73-19.73c-.78 0-1.51-.23-2.11-.64l-6.58 5.71c.24.5.38 1.05.38 1.64 0 2.1-1.7 3.8-3.79 3.8s-3.8-1.7-3.8-3.8c0-.37.07-.72.16-1.06l-6.33-3.88c-.68.63-1.58 1.03-2.59 1.03-.72 0-1.38-.21-1.95-.56l-6.6 6.27c.29.54.47 1.14.47 1.79 0 2.1-1.7 3.8-3.8 3.8s-3.79-1.7-3.79-3.8 1.7-3.8 3.79-3.8c.72 0 1.38.21 1.96.56l6.59-6.27c-.29-.53-.47-1.14-.47-1.79 0-2.09 1.71-3.79 3.8-3.79s3.8 1.7 3.8 3.79c0 .37-.07.72-.17 1.06l6.34 3.89c.68-.64 1.58-1.04 2.59-1.04.77 0 1.5.24 2.1.65l6.58-5.72c-.24-.49-.38-1.04-.38-1.63 0-2.1 1.7-3.8 3.8-3.8s3.79 1.7 3.79 3.8-1.7 3.79-3.79 3.79z"/><path fill="currentColor" opacity="0.4" d="m20.91 54.773c-1.55 0-2.811 1.265-2.811 2.82v.76h27.801v-.76c0-1.555-1.261-2.82-2.811-2.82z"/><path fill="currentColor" opacity="0.4" d="m23.59 48.281h16.82v4.492h-16.82z"/></svg>
                      </span>
                      <span className="nav-text">
                        <FormattedMessage id="dashboard" />
                      </span>
                    </NavLinkWithCheck>
                  </li>
                )}
                {findRouteAccess(ModuleType.CustomerManagement) && (
                  <li title={formatMessage("customer_management")}>
                    <NavLinkWithCheck to={RoutesConstants.CustomerManagement}>
                      <span className="svgWidth">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="23"
                          height="23"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            opacity=".4"
                            d="M9 2C6.38 2 4.25 4.13 4.25 6.75c0 2.57 2.01 4.65 4.63 4.74.08-.01.16-.01.22 0h.07a4.738 4.738 0 0 0 4.58-4.74C13.75 4.13 11.62 2 9 2Z"
                            fill="currentColor"
                          ></path>
                          <path
                            d="M14.08 14.149c-2.79-1.86-7.34-1.86-10.15 0-1.27.85-1.97 2-1.97 3.23s.7 2.37 1.96 3.21c1.4.94 3.24 1.41 5.08 1.41 1.84 0 3.68-.47 5.08-1.41 1.26-.85 1.96-1.99 1.96-3.23-.01-1.23-.7-2.37-1.96-3.21Z"
                            fill="currentColor"
                          ></path>
                          <path
                            opacity=".4"
                            d="M19.99 7.338c.16 1.94-1.22 3.64-3.13 3.87h-.05c-.06 0-.12 0-.17.02-.97.05-1.86-.26-2.53-.83 1.03-.92 1.62-2.3 1.5-3.8a4.64 4.64 0 0 0-.77-2.18 3.592 3.592 0 0 1 5.15 2.92Z"
                            fill="currentColor"
                          ></path>
                          <path
                            d="M21.988 16.59c-.08.97-.7 1.81-1.74 2.38-1 .55-2.26.81-3.51.78.72-.65 1.14-1.46 1.22-2.32.1-1.24-.49-2.43-1.67-3.38-.67-.53-1.45-.95-2.3-1.26 2.21-.64 4.99-.21 6.7 1.17.92.74 1.39 1.67 1.3 2.63Z"
                            fill="currentColor"
                          ></path>
                        </svg>
                      </span>
                      <span className="nav-text">
                        {<FormattedMessage id="customer_management" />}
                      </span>
                    </NavLinkWithCheck>
                  </li>
                )}
                {findModueOptionsExist([
                  ModuleType.ReservationManagement,
                  ModuleType.CancelledReservations,
                ]) && (
                  <li
                    title={formatMessage("reservation_management")}
                    className={`dropdown ${
                      isReservationSettingsActive ? "active" : ""
                    }`}
                  >
                    <a
                      data-bs-auto-close="outside"
                      data-bs-toggle="dropdown"
                      className="dropdown-toggle"
                    >
                      <span className="svgWidth">
                        <svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="24"
                          height="24"
                            viewBox="0 0 512 512" >
                          <g id="Ikon">
                            <path fill="currentColor" opacity="0.4" d="M480,256c0,123.7-100.3,224-224,224S32,379.7,32,256c0-8.8,7.2-16,16-16s16,7.2,16,16c-0.2,106.2,85.8,192.4,192,192.6
                              s192.4-85.8,192.6-192S362.8,64.2,256.6,64c-37.9-0.1-74.9,11.1-106.5,32h9.9c8.8,0,16,7.2,16,16s-7.2,16-16,16h-48
                              c-8.8,0-16-7.2-16-16c0,0,0,0,0,0V64c0-8.8,7.2-16,16-16s16,7.2,16,16v8.2c101.5-70.7,241.1-45.8,311.8,55.7
                              C466,165.5,480,210.2,480,256z"/>
                          </g>
                          <g id="Ikon_00000082350405533031087730000006665989449379645583_">
                            <path fill="currentColor" d="M399.5,255.7c0,79.5-64.5,144-144,144s-144-64.5-144-144s64.5-144,144-144C335,111.8,399.4,176.3,399.5,255.7z
                              M312.4,274.4l-40.9-27.3v-71.4c0-8.8-7.2-16-16-16s-16,7.2-16,16v80c0,5.3,2.7,10.3,7.1,13.3l48,32c7.4,4.8,17.3,2.7,22.1-4.7
                              C321.5,289.1,319.6,279.3,312.4,274.4z"/>
                          </g>
                          </svg>

                      </span>
                      <span className="nav-text">
                        {<FormattedMessage id="reservation_management" />}
                      </span>
                    </a>
                    <ul className="dropdown-menu">
                      {/* First submenu item */}
                      {findModueOptionsExist([
                        ModuleType.ReservationManagement,
                      ]) && (
                        <li>
                          <NavLinkWithCheck
                            className="dropdown-item"
                            to={RoutesConstants.ReservationManagement}
                            title={formatMessage("reservation_management")}
                          >
                            {<FormattedMessage id="reservation_management" />}
                          </NavLinkWithCheck>
                        </li>
                      )}
                      {findRouteAccess(ModuleType.CancelledReservations) && (
                        <li>
                          <NavLinkWithCheck
                            className="dropdown-item"
                            to={RoutesConstants.CancelledReservation}
                            title={formatMessage("cancelled_reservation")}
                          >
                            {<FormattedMessage id="cancelled_reservation" />}
                          </NavLinkWithCheck>
                        </li>
                      )}
                    </ul>
                  </li>
                )}
                {findRouteAccess(ModuleType.ShiftManagement) && (
                  <li title={formatMessage("shift_management")}>
                    <NavLinkWithCheck to={RoutesConstants.ShiftManagement}>
                      <span className="svgWidth">
                        <svg clip-rule="evenodd" fill-rule="evenodd" height="24" stroke-linejoin="round" stroke-miterlimit="2" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg"><g id="Icon"><path d="m6 2.75h-1c-2.071 0-3.75 1.679-3.75 3.75v12c0 2.071 1.679 3.75 3.75 3.75h7.876c-.346-.678-.398-1.475-.131-2.2.469-1.28 1.378-2.351 2.543-3.027-.692-.719-1.118-1.697-1.118-2.773 0-2.208 1.792-4 4-4 .983 0 1.883.355 2.58.944v-4.694c0-2.071-1.679-3.75-3.75-3.75h-1v2.25c0 .414-.336.75-.75.75s-.75-.336-.75-.75v-2.25h-7v2.25c0 .414-.336.75-.75.75s-.75-.336-.75-.75zm0 12h5c.414 0 .75-.336.75-.75s-.336-.75-.75-.75h-5c-.414 0-.75.336-.75.75s.336.75.75.75zm0-4h3.1c.414 0 .75-.336.75-.75s-.336-.75-.75-.75h-3.1c-.414 0-.75.336-.75.75s.336.75.75.75z"/><circle  fill="currentColor" opacity="0.4" cx="18.17" cy="14.25" r="3"/><path  fill="currentColor" opacity="0.4" d="m15.327 22.75 5.693-.013c.561 0 1.093-.278 1.418-.744s.401-1.061.205-1.594c-.653-1.831-2.41-3.149-4.473-3.149-2.06 0-3.816 1.314-4.486 3.145-.198.537-.121 1.137.207 1.606.327.47.864.749 1.436.749z"/><path  fill="currentColor" opacity="0.4" d="m6 2.75h1.5v-.75c0-.414-.336-.75-.75-.75s-.75.336-.75.75z"/><path  fill="currentColor" opacity="0.4" d="m14.5 2.75h1.5v-.75c0-.414-.336-.75-.75-.75s-.75.336-.75.75z"/></g></svg>
                      </span>
                      <span className="nav-text">
                        {<FormattedMessage id="shift_management" />}
                      </span>
                    </NavLinkWithCheck>
                  </li>
                )}
                {findModueOptionsExist([ModuleType.SalesMedicalRecord]) && (
                  <li
                    title={formatMessage("store_management")}
                    className={`dropdown ${isStoreActive ? "active" : ""}`}
                  >
                    <a
                      data-bs-auto-close="outside"
                      data-bs-toggle="dropdown"
                      className="dropdown-toggle"
                    >
                      <span className="svgWidth">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M9.85714 3H4.14286C3.51167 3 3 3.51167 3 4.14286V9.85714C3 10.4883 3.51167 11 4.14286 11H9.85714C10.4883 11 11 10.4883 11 9.85714V4.14286C11 3.51167 10.4883 3 9.85714 3Z"
                            fill="currentColor"
                          ></path>
                          <path
                            d="M9.85714 12.8999H4.14286C3.51167 12.8999 3 13.4116 3 14.0428V19.757C3 20.3882 3.51167 20.8999 4.14286 20.8999H9.85714C10.4883 20.8999 11 20.3882 11 19.757V14.0428C11 13.4116 10.4883 12.8999 9.85714 12.8999Z"
                            fill="currentColor"
                            fillOpacity="0.3"
                          ></path>
                          <path
                            d="M19.757 3H14.0428C13.4116 3 12.8999 3.51167 12.8999 4.14286V9.85714C12.8999 10.4883 13.4116 11 14.0428 11H19.757C20.3882 11 20.8999 10.4883 20.8999 9.85714V4.14286C20.8999 3.51167 20.3882 3 19.757 3Z"
                            fill="currentColor"
                            fillOpacity="0.3"
                          ></path>
                          <path
                            d="M19.757 12.8999H14.0428C13.4116 12.8999 12.8999 13.4116 12.8999 14.0428V19.757C12.8999 20.3882 13.4116 20.8999 14.0428 20.8999H19.757C20.3882 20.8999 20.8999 20.3882 20.8999 19.757V14.0428C20.8999 13.4116 20.3882 12.8999 19.757 12.8999Z"
                            fill="currentColor"
                            fillOpacity="0.3"
                          ></path>
                        </svg>
                      </span>
                      <span className="nav-text">
                        {<FormattedMessage id="store_management" />}
                      </span>
                    </a>
                    <ul className="dropdown-menu">
                      {findRouteAccess(ModuleType.StaffManagement) && (
                        <li>
                          <NavLinkWithCheck
                            className="dropdown-item"
                            to={RoutesConstants.StoreDetails}
                            title={formatMessage("store_information")}
                          >
                            <FormattedMessage id="store_information" />
                          </NavLinkWithCheck>
                        </li>
                      )}
                      {findRouteAccess(ModuleType.StaffManagement) && (
                        <li>
                          <NavLinkWithCheck
                            className="dropdown-item"
                            to={RoutesConstants.StoreStaffInformation}
                            title={formatMessage("staff_information")}
                          >
                            <FormattedMessage id="staff_information" />
                          </NavLinkWithCheck>
                        </li>
                      )}
                      {findRouteAccess(ModuleType.TreatmentManagement) && (
                        <li>
                          <NavLinkWithCheck
                            className="dropdown-item"
                            to={RoutesConstants.StoreTreatmentList}
                            title={formatMessage("treatment_management")}
                          >
                            <FormattedMessage id="treatment_management" />
                          </NavLinkWithCheck>
                        </li>
                      )}
                      {findRouteAccess(ModuleType.ProductManagement) && (
                        <li>
                          <NavLinkWithCheck
                            className="dropdown-item"
                            to={RoutesConstants.StoreProductList}
                            title={formatMessage("product_management")}
                          >
                            <FormattedMessage id="product_management" />
                          </NavLinkWithCheck>
                        </li>
                      )}
                      {findRouteAccess(ModuleType.OptionsManagement) && (
                        <li>
                          <NavLinkWithCheck
                            className="dropdown-item"
                            to={RoutesConstants.StoreOptionMenuList}
                            title={formatMessage("option_management")}
                          >
                            <FormattedMessage id="option_management" />
                          </NavLinkWithCheck>
                        </li>
                      )}

                    </ul>
                  </li>
                )}
                {findModueOptionsExist([
                  ModuleType.AgreementManegement,
                  ModuleType.AgreementPasswordSetting,
                  ModuleType.TaxSetting,
                  ModuleType.TimeSetting,
                  ModuleType.BusinessTypeManagement,
                  ModuleType.StoreMotivationSetting,
                  ModuleType.PaymentModeSetting,
                  ModuleType.ReservationSetting,
                  ModuleType.ConsumptionTaxSetting,
                  ModuleType.UploadIconsSetting,
                  ModuleType.UploadTemplatesSetting,
                  ModuleType.StoreCMS,
                  ModuleType.EmailManagement
                ]) && (
                  <li
                    title={formatMessage("various_settings")}
                    className={`dropdown ${
                      isVariousSettingsActive ? "active" : ""
                    }`}
                  >
                    <a
                      data-bs-auto-close="outside"
                      data-bs-toggle="dropdown"
                      className="dropdown-toggle"
                    >
                      <span className="svgWidth">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            opacity=".4"
                            d="M2 12.881v-1.76c0-1.04.85-1.9 1.9-1.9 1.81 0 2.55-1.28 1.64-2.85-.52-.9-.21-2.07.7-2.59l1.73-.99c.79-.47 1.81-.19 2.28.6l.11.19c.9 1.57 2.38 1.57 3.29 0l.11-.19c.47-.79 1.49-1.07 2.28-.6l1.73.99c.91.52 1.22 1.69.7 2.59-.91 1.57-.17 2.85 1.64 2.85 1.04 0 1.9.85 1.9 1.9v1.76c0 1.04-.85 1.9-1.9 1.9-1.81 0-2.55 1.28-1.64 2.85.52.91.21 2.07-.7 2.59l-1.73.99c-.79.47-1.81.19-2.28-.6l-.11-.19c-.9-1.57-2.38-1.57-3.29 0l-.11.19c-.47.79-1.49 1.07-2.28.6l-1.73-.99a1.899 1.899 0 0 1-.7-2.59c.91-1.57.17-2.85-1.64-2.85-1.05 0-1.9-.86-1.9-1.9Z"
                            fill="currentColor"
                          ></path>
                          <path
                            d="M12 15.25a3.25 3.25 0 1 0 0-6.5 3.25 3.25 0 0 0 0 6.5Z"
                            fill="currentColor"
                          ></path>
                        </svg>
                      </span>
                      <span className="nav-text">
                        {<FormattedMessage id="various_settings" />}
                      </span>
                    </a>
                    <ul className="dropdown-menu">
                      {/* First submenu item */}
                      {findRouteAccess(ModuleType.ReservationSetting) && (
                        <li>
                          <NavLinkWithCheck
                            className="dropdown-item"
                            to={{
                              pathname: RoutesConstants.ReservationSettings,
                              state: { storeId: 0, fromNavbar: true },
                            }}
                            title={formatMessage("reservation_settings")}
                          >
                            <FormattedMessage id="reservation_settings" />
                          </NavLinkWithCheck>
                        </li>
                      )}

                      {findRouteAccess(ModuleType.AgreementPasswordSetting) && (
                        <li>
                          <NavLinkWithCheck
                            className="dropdown-item"
                            to={RoutesConstants.ChaneAgreementPassword}
                            title={formatMessage("agreement_password_setting")}
                          >
                            <FormattedMessage id="agreement_password_setting" />
                          </NavLinkWithCheck>
                        </li>
                      )}
                      {findRouteAccess(ModuleType.TaxSetting) && (
                        <li>
                          <NavLinkWithCheck
                            className="dropdown-item"
                            to={RoutesConstants.TaxSettings}
                          >
                            {<FormattedMessage id="tax_setting" />}
                          </NavLinkWithCheck>
                        </li>
                      )}
                      {findRouteAccess(ModuleType.TimeSetting) && (
                        <li>
                          <NavLinkWithCheck
                            className="dropdown-item"
                            to={RoutesConstants.TimeSettings}
                          >
                            {<FormattedMessage id="time_setting" />}
                          </NavLinkWithCheck>
                        </li>
                      )}
                      {findRouteAccess(ModuleType.BusinessTypeManagement) && (
                        <li>
                          <NavLinkWithCheck
                            className="dropdown-item"
                            to={RoutesConstants.BusinessTypeSettings}
                          >
                            {<FormattedMessage id="business_type_setting" />}
                          </NavLinkWithCheck>
                        </li>
                      )}

                      {findRouteAccess(ModuleType.ConsumptionTaxSetting) && (
                        <li>
                          <NavLinkWithCheck
                            className="dropdown-item"
                            to={RoutesConstants.ConsumptionTax}
                          >
                            {<FormattedMessage id="consumption_tax" />}
                          </NavLinkWithCheck>
                        </li>
                      )}
                      {findRouteAccess(ModuleType.UploadIconsSetting) && (
                        <li>
                          <NavLinkWithCheck
                            className="dropdown-item"
                            to={RoutesConstants.UploadIcons}
                          >
                            {<FormattedMessage id="upload_icons" />}
                          </NavLinkWithCheck>
                        </li>
                      )}
                      {findRouteAccess(ModuleType.UploadTemplatesSetting) && (
                        <li>
                          <NavLinkWithCheck
                            className="dropdown-item"
                            to={RoutesConstants.UploadTemplates}
                          >
                            {<FormattedMessage id="upload_templates" />}
                          </NavLinkWithCheck>
                        </li>
                      )}
                      {findRouteAccess(ModuleType.StoreMotivationSetting) && (
                        <li>
                          <NavLinkWithCheck
                            className="dropdown-item"
                            to={RoutesConstants.MotivationInStore}
                            title={formatMessage("store_motivation_settings")}
                          >
                            {
                              <FormattedMessage id="store_motivation_settings" />
                            }
                          </NavLinkWithCheck>
                        </li>
                      )}
                      {findRouteAccess(ModuleType.PaymentModeSetting) && (
                        <li>
                          <NavLinkWithCheck
                            className="dropdown-item"
                            to={RoutesConstants.PaymentInStore}
                            title={formatMessage("payment_mode_settings")}
                          >
                            {<FormattedMessage id="payment_mode_settings" />}
                          </NavLinkWithCheck>
                        </li>
                      )}
                      {findRouteAccess(ModuleType.StoreCMS) && (
                        <li>
                          <NavLinkWithCheck
                            className="dropdown-item"
                            to={RoutesConstants.StoreCmsDetails}
                            title={formatMessage("cms_management")}
                          >
                            <FormattedMessage id="cms_management" />
                          </NavLinkWithCheck>
                        </li>
                      )}
                      <li>
                        <NavLinkWithCheck
                          className="dropdown-item"
                          to={RoutesConstants.ThemeInStore}
                          title={formatMessage("theme_settings")}
                        >
                          {<FormattedMessage id="theme_settings" />}
                        </NavLinkWithCheck>
                      </li>
                      {findRouteAccess(ModuleType.EmailManagement) && (
                        <li>
                          <NavLinkWithCheck
                            className="dropdown-item"
                            to={RoutesConstants.EmailManagement}
                          >
                            {<FormattedMessage id="email_management" />}
                          </NavLinkWithCheck>
                        </li>
                      )}
                    </ul>
                  </li>
                )}
                {findRouteAccess(ModuleType.InventoryManagement) && (
                  <li title={formatMessage("inventory_management")}>
                    <NavLinkWithCheck to={RoutesConstants.InventoryManagement}>
                      <span className="svgWidth">
                        <svg version="1.1" id="Layer_1" x="0px" y="0px" width="26"
                          height="26"
                          viewBox="0 0 512 512" >
                        <path fill="currentColor" opacity="0.4" d="M268.5,19.9c-3.2-1.8-6.8-1.8-10,0L142.7,86.7c-3.2,1.8-5,5-5,8.7V229c0,3.7,1.8,6.8,5,8.7l115.7,66.8
                          c3.2,1.8,6.8,1.8,10,0l115.7-66.8c3.2-1.8,5-5,5-8.7V95.4c0-3.7-1.8-6.8-5-8.7l-39.6-22.9l-125.7,72.6v52c0,2.3-2.5,3.8-4.5,2.6
                          l-30.5-17.6c-1-0.6-1.5-1.5-1.5-2.6v-55.4l125.7-72.6L268.5,19.9z"/>
                        <path fill="currentColor"  d="M259.9,321v38.9c0,1.1,0.5,2,1.5,2.6l30.5,17.6c2,1.2,4.5-0.3,4.5-2.6v-52L422.1,253l39.6,22.9
                          c3.2,1.8,5,5,5,8.7v133.6c0,3.7-1.8,6.8-5,8.7L346,493.6c-3.2,1.8-6.8,1.8-10,0l-115.7-66.8c-3.2-1.8-5-5-5-8.7V297.2l35.7,20.6
                          C253.8,319.5,256.8,320.5,259.9,321L259.9,321z M122.2,188.9l-31.3,18.1v55.4c0,1.1,0.5,2,1.5,2.6l30.5,17.6c2,1.2,4.5-0.3,4.5-2.6
                          v-35.9c2.1,2.8,4.7,5.1,7.9,7l64,37v95.2L177,396.1c-3.2,1.8-6.8,1.8-10,0L51.3,329.2c-3.2-1.8-5-5-5-8.7V186.9c0-3.7,1.8-6.8,5-8.7
                          l71-41L122.2,188.9z"/>
                        </svg>
                      </span>
                      <span className="nav-text">
                        {<FormattedMessage id="inventory_management" />}
                      </span>
                    </NavLinkWithCheck>
                  </li>
                )}
                {findModueOptionsExist([
                  ModuleType.TotallingManagement
                ]) && (
                  <li
                    title={formatMessage("aggregate")}
                    className={`dropdown ${
                      isTotallingManagementRoutes ? "active" : ""
                    }`}
                  >
                    <a
                      data-bs-auto-close="outside"
                      data-bs-toggle="dropdown"
                      className="dropdown-toggle"
                    >
                      <span className="svgWidth">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M21.67 6.95c-.64-2.17-2.45-3.98-4.62-4.62-1.65-.48-2.79-.44-3.58.15-.95.71-1.06 1.99-1.06 2.9v2.49c0 2.46 1.12 3.71 3.32 3.71h2.87c.9 0 2.19-.11 2.9-1.06.61-.78.66-1.92.17-3.57Z"
                            fill="currentColor"
                          ></path>
                          <path
                            opacity=".4"
                            d="M18.91 13.361c-.26-.3-.64-.47-1.03-.47H14.3c-1.76 0-3.19-1.43-3.19-3.19v-3.58c0-.39-.17-.77-.47-1.03-.29-.26-.69-.38-1.07-.33-2.35.3-4.51 1.59-5.92 3.53-1.42 1.95-1.94 4.33-1.49 6.71.65 3.44 3.4 6.19 6.85 6.84.55.11 1.1.16 1.65.16 1.81 0 3.56-.56 5.05-1.65 1.94-1.41 3.23-3.57 3.53-5.92.05-.39-.07-.78-.33-1.07Z"
                            fill="currentColor"
                          ></path>
                        </svg>
                      </span>
                      <span className="nav-text">
                        {<FormattedMessage id="aggregate" />}
                      </span>
                    </a>
                    <ul className="dropdown-menu">
                      {/* First submenu item */}
                      {findModueOptionsExist([
                        ModuleType.TotallingManagement,
                      ]) && (
                        <li>
                          <NavLinkWithCheck
                            className="dropdown-item"
                            to={RoutesConstants.TotallingManagementDaily}
                            title={formatMessage("daily_sales")}
                          >
                            <FormattedMessage id="daily_sales" />
                          </NavLinkWithCheck>
                        </li>
                      )}
                      {findRouteAccess(ModuleType.TotallingManagement) && (
                        <li>
                          <NavLinkWithCheck
                            className="dropdown-item"
                            aria-disabled={true}
                            to={RoutesConstants.TotallingManagementWeekly}
                            title={formatMessage("weekly_sales")}
                          >
                            <FormattedMessage id="weekly_sales" />
                          </NavLinkWithCheck>
                        </li>
                      )}
                      {findRouteAccess(ModuleType.TotallingManagement) && (
                        <li>
                          <NavLinkWithCheck
                            className="dropdown-item"
                            aria-disabled={true}
                            to={RoutesConstants.TotallingManagementMonthly}
                            title={formatMessage("monthly_sales")}
                          >
                            <FormattedMessage id="monthly_sales" />
                          </NavLinkWithCheck>
                        </li>
                      )}
                      {findRouteAccess(ModuleType.TotallingManagement) && (
                        <li>
                          <NavLinkWithCheck
                            className="dropdown-item"
                            aria-disabled={true}
                            to={RoutesConstants.TotallingManagementPeriodSales}
                            title={formatMessage("period_totalling")}
                          >
                            <FormattedMessage id="period_totalling" />
                          </NavLinkWithCheck>
                        </li>
                      )}
                      {findRouteAccess(ModuleType.TotallingManagement) && (
                        <li>
                          <NavLinkWithCheck
                            className="dropdown-item"
                            aria-disabled={true}
                            to={RoutesConstants.TotallingManagementStaffSales}
                            title={formatMessage("staff_sales")}
                          >
                            <FormattedMessage id="staff_sales" />
                          </NavLinkWithCheck>
                        </li>
                      )}
                    </ul>
                  </li>
                )}
              </React.Fragment>
            </ul>
          </nav>
        </div>
        <div className="aside-footer">
          <ul className="p-0">
            <li className="logout" title={formatMessage("store_details")}>
              <div className="nav-text ms-1">
                <div className="d-flex align-items-center">
                  <div className="me-2">
                    <div className="profile_modal_icon">
                      <svg
                        width="24"
                        height="24"
                        fill="#fff"
                        id="_レイヤー_6"
                        data-name="レイヤー 6"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 30 30"
                      >
                        <g>
                          <circle cx="14.95" cy="7.61" r="3.75"></circle>
                          <path
                            fill="#fff"
                            d="M14.95,12.08c-2.46,0-4.47-2.01-4.47-4.47s2-4.47,4.47-4.47,4.47,2.01,4.47,4.47-2,4.47-4.47,4.47Zm0-7.5c-1.67,0-3.03,1.36-3.03,3.03s1.36,3.03,3.03,3.03,3.03-1.36,3.03-3.03-1.36-3.03-3.03-3.03Z"
                          ></path>
                        </g>
                        <path
                          fill="#fff"
                          d="M24.95,26.86h-3v-.8c0-3.2-.91-7.15-2.27-9.84-.52-1.04-1.75-.95-3.41-.72-1.04,.15-1.75,.13-2.68-.01-1.63-.24-2.83-.34-3.34,.65-1.27,2.48-2.16,6.05-2.29,9.12h9.49c-.09-1.15-.3-3.27-.76-4.73l1.52-.48c.72,2.3,.87,5.83,.88,5.97l.03,.83H6.34v-.8c0-3.52,.98-7.71,2.49-10.65,1.07-2.09,3.44-1.73,5-1.5,.84,.13,1.39,.13,2.22,0,1.59-.22,3.98-.55,5.06,1.58,1.37,2.72,2.3,6.47,2.43,9.76h1.42v1.6Z"
                        ></path>
                      </svg>
                    </div>
                  </div>
                  <div className="user-details">
                    <p className="m-0 text-white">
                      {storeCode ? storeCode : emptyValueNA}
                    </p>
                    <small className="text-white">
                      {getUserType ? formatMessage(getUserType) : ""}
                    </small>
                  </div>
                </div>
              </div>
              <div
                className="profile_svg"
                data-testid="Logout"
                title={formatMessage("logout")}
                onClick={props.logoutPopupToggleHandler}
                // onClick={async()=>{
                //   if (smrStore.isDataChanged) {
                //     const confirmed = await showConfirmDialog(
                //       "confirmation",
                //       "unsaved_changes_warning"
                //     );
                //     if (!confirmed) return;
                //     smrStore.setIsDataChanged(false); // reset if confirmed
                //   }
                //   props.logoutPopupToggleHandler();
                // }}
              >
                <svg
                  version="1.1"
                  id="fi_992680"
                  x="0px"
                  y="0px"
                  viewBox="0 0 512 512"
                  fill="currentColor"
                >
                  <g>
                    <g>
                      <path
                        d="M255.15,468.625H63.787c-11.737,0-21.262-9.526-21.262-21.262V64.638c0-11.737,9.526-21.262,21.262-21.262H255.15
                        c11.758,0,21.262-9.504,21.262-21.262S266.908,0.85,255.15,0.85H63.787C28.619,0.85,0,29.47,0,64.638v382.724
                        c0,35.168,28.619,63.787,63.787,63.787H255.15c11.758,0,21.262-9.504,21.262-21.262
                        C276.412,478.129,266.908,468.625,255.15,468.625z"
                      ></path>
                    </g>
                  </g>
                  <g>
                    <g>
                      <path
                        d="M505.664,240.861L376.388,113.286c-8.335-8.25-21.815-8.143-30.065,0.213s-8.165,21.815,0.213,30.065l92.385,91.173
                        H191.362c-11.758,0-21.262,9.504-21.262,21.262c0,11.758,9.504,21.263,21.262,21.263h247.559l-92.385,91.173
                        c-8.377,8.25-8.441,21.709-0.213,30.065c4.167,4.21,9.653,6.336,15.139,6.336c5.401,0,10.801-2.041,14.926-6.124l129.276-127.575
                        c4.04-3.997,6.336-9.441,6.336-15.139C512,250.302,509.725,244.88,505.664,240.861z"
                      ></path>
                    </g>
                  </g>
                </svg>
              </div>
            </li>
          </ul>
        </div>
      </aside>
    </React.Fragment>
  );
};

export default UserProfilePopupHOC(
  LogoutPopupHOC(
    TaxSettingPopupHOC(
      TimeSettingPopupHOC(
        BusinessTypeSettingPopupHOC(injectIntl(observer(LeftNavBar)))
      )
    )
  )
);
